/**
 * Modelo de datos de Firestore compartido entre la web y las Functions.
 * Las fechas se tipan como `unknown` en los documentos crudos porque llegan como Timestamp
 * (cliente o admin); cada lado las convierte con su propio SDK.
 */

export const COLLECTIONS = {
  campaigns: 'campaigns',
  assets: 'assets',
  landing: 'landing',
  members: 'members',
  syncRuns: 'syncRuns',
  generations: 'generations',
  feedback: 'feedback',
  rateLimits: 'rateLimits',
  campaignSlugs: 'campaignSlugs',
  brands: 'brands',
  brandSlugs: 'brandSlugs',
  /** Tema público por subdominio (nombre y colores) para el login del portal, antes de entrar. */
  brandThemes: 'brandThemes',
  /** Caché de URLs firmadas (solo Functions): evita volver a firmar en cada visita. */
  signedUrls: 'signedUrls',
} as const;

export const LANDING_DOCS = { draft: 'draft', published: 'published' } as const;

export type CampaignStatus = 'draft' | 'published';

/**
 * Paleta de acentos (ver web/src/styles/tokens.ts): colores de brands.mx, incluidos los de la
 * franja de su logo. Además de un nombre de la paleta, el acento puede ser un color propio en
 * hexadecimal (#rrggbb) para usar el color exacto de una marca.
 */
export const ACCENT_NAMES = [
  'navy',
  'blue',
  'deepBlue',
  'sky',
  'mint',
  'green',
  'lime',
  'yellow',
  'tangerine',
  'amber',
  'orange',
  'coral',
  'red',
  'magenta',
  'purple',
  'lilac',
  'mauve',
  'midnight',
  'black',
] as const;

export type AccentName = (typeof ACCENT_NAMES)[number];

/** Color propio de una marca en hexadecimal, p. ej. "#0a6ebd". */
export type CustomAccent = `#${string}`;
export type Accent = AccentName | CustomAccent;

const HEX_ACCENT = /^#[0-9a-f]{6}$/i;

export const isCustomAccent = (value: unknown): value is CustomAccent =>
  typeof value === 'string' && HEX_ACCENT.test(value);

/** Acento válido: nombre de la paleta o color propio (#rrggbb, se guarda en minúsculas). */
export function parseAccent(value: unknown): Accent | null {
  if ((ACCENT_NAMES as readonly unknown[]).includes(value)) return value as AccentName;
  if (isCustomAccent(value)) return value.toLowerCase() as CustomAccent;
  return null;
}

export type AssetKind = 'image' | 'video' | 'audio' | 'pdf' | 'doc' | 'other';

export interface CampaignDoc {
  name: string;
  clientName: string;
  accent: string;
  driveFolderId: string;
  driveFolderUrl: string;
  driveId: string | null;
  driveResourceKey: string | null;
  driveFolderName: string;
  /** Link público: {clientSlug}.orbyte.mx/{slug} (o /{clientSlug}/{slug} por ruta). */
  clientSlug: string;
  slug: string;
  /** Marca a la que pertenece (null en campañas sueltas anteriores a las marcas). */
  brandId?: string | null;
  /** Carpeta raíz de la marca de la que salió (si se creó por escaneo). */
  brandRootFolderId?: string | null;
  /** Pieza de portada para la tarjeta de la campaña (se fija al publicar). */
  coverAssetId?: string | null;
  /** Estado del proceso automático: sincronizar → copiar → generar. */
  pipeline?: CampaignPipeline | null;
  status: CampaignStatus;
  /** Correos invitados, normalizados. Es la fuente de verdad del acceso en las reglas. */
  memberEmails: string[];
  lastSync: (SyncSummary & { at: unknown }) | null;
  lastGeneration?: GenerationSummary | null;
  /** Candado para evitar dos sincronizaciones simultáneas. */
  syncLock: { startedAt: unknown; by: string } | null;
  createdBy: string;
  createdAt: unknown;
  updatedAt: unknown;
  publishedAt: unknown | null;
}

/** Fase 2 opcional (aprobaciones/comentarios por pieza): solo modelo, sin UI todavía. */
export interface FeedbackDoc {
  assetId: string;
  uid: string;
  email: string;
  type: 'approve' | 'comment' | 'changes';
  text: string;
  resolved: boolean;
  createdAt: unknown;
}

export type CopyStatus = 'pending' | 'copied' | 'error' | 'skipped' | 'removed';

export interface AssetDoc {
  driveFileId: string;
  name: string;
  mimeType: string;
  kind: AssetKind;
  size: number | null;
  version: string;
  modifiedTime: string;
  /** Ruta de subcarpetas dentro de la carpeta de la campaña, p. ej. "02_Visuales/Redes". */
  path: string;
  pathSegments: string[];
  isContextDoc: boolean;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  copyStatus: CopyStatus;
  copyError: string | null;
  copiedVersion: string | null;
  storagePath: string | null;
  contentType: string | null;
  thumbPath: string | null;
  thumbLargePath: string | null;
  firstSeenAt: unknown;
  contentUpdatedAt: unknown;
  syncedAt: unknown;
  removedAt: unknown | null;
}

export interface SyncSummary {
  /** `needs-confirmation`: la copia rebasa el límite de seguridad y el admin debe confirmarla. */
  status: 'ok' | 'error' | 'running' | 'needs-confirmation';
  added: number;
  modified: number;
  removed: number;
  unchanged: number;
  skipped: number;
  total: number;
  /** Archivos y bytes que esta sincronización manda (o mandaría) a copiar. */
  toCopy: number;
  toCopyBytes: number;
  error: string | null;
}

/** Límites de seguridad: arriba de esto, la sync se detiene y pide confirmación. */
export const COPY_CONFIRM_LIMITS = { bytes: 20 * 1024 ** 3, files: 2000 } as const;

export function needsCopyConfirmation(toCopy: number, toCopyBytes: number): boolean {
  return toCopyBytes > COPY_CONFIRM_LIMITS.bytes || toCopy > COPY_CONFIRM_LIMITS.files;
}

/** Resumen de la última generación de landing (en el documento de la campaña). */
export interface GenerationSummary {
  status: 'running' | 'ok' | 'error';
  at?: unknown;
  startedAt?: unknown;
  by?: string;
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  pendingCount?: number;
  warningsCount?: number;
  durationMs?: number;
  generationId?: string;
  error?: string;
}

export interface LandingSourceDoc {
  id: string;
  name: string;
  path: string;
  type: 'pdf' | 'texto';
  chars: number;
  truncated: boolean;
}

export type PipelineStage =
  'queued' | 'syncing' | 'copying' | 'generating' | 'ready' | 'needs-confirmation' | 'error';

export interface CampaignPipeline {
  stage: PipelineStage;
  message: string | null;
  updatedAt: unknown;
}

export interface BrandRootFolder {
  folderId: string;
  url: string;
  name: string;
  driveId: string | null;
  resourceKey: string | null;
}

export interface BrandDomainStatus {
  host: string;
  state: 'pending' | 'active' | 'error' | 'unregistered';
  detail: string | null;
  checkedAt: unknown;
}

export interface BrandDoc {
  name: string;
  /** Subdominio: {slug}.orbyte.mx. No cambia después de crear la marca. */
  slug: string;
  accent: string;
  /** Color secundario (círculo del portal, login). null = se calcula a partir del principal. */
  accent2?: string | null;
  roots: BrandRootFolder[];
  /** Personas que pueden entrar al portal de la marca. Cada campaña se habilita aparte. */
  memberEmails: string[];
  domain: BrandDomainStatus | null;
  lastScan: { at: unknown; created: number; by: string } | null;
  createdBy: string;
  createdAt: unknown;
  updatedAt: unknown;
}

/**
 * Tema público de una marca en `brandThemes/{slug}`: lo único que se muestra antes de iniciar
 * sesión en su portal. Solo se lee por subdominio (no se pueden listar las marcas).
 */
export interface BrandTheme {
  name: string;
  accent: string;
  accent2: string | null;
}
